import {
  WorkspaceInvitationsApi,
  type WorkspaceMemberWithDetailsDto,
} from '@up2stay/up2promo-api-client';
import { useEffect, useRef } from 'react';

import { useAuthStore } from '@/core/store/auth.store';

import { createHydratedStorage } from '../store/hydrated.store';

interface PendingInvitationsState {
  invitations: WorkspaceMemberWithDetailsDto[];
  isLoading: boolean;
  error: Error | null;
  setInvitations: (invitations: WorkspaceMemberWithDetailsDto[]) => void;
  setIsLoading: (isLoading: boolean) => void;
  setError: (error: Error | null) => void;
  refetch: () => Promise<void>;
}

export const usePendingInvitationsStore =
  createHydratedStorage<PendingInvitationsState>(
    (set) => ({
      invitations: [],
      isLoading: true,
      error: null,
      setInvitations: (invitations) => set({ invitations }),
      setIsLoading: (isLoading) => set({ isLoading }),
      setError: (error) => set({ error }),
      refetch: async () => {
        const authState = useAuthStore.getState();
        const { user, tokens, hydrated } = authState;

        // Wait for store hydration before making requests
        if (!hydrated) {
          return;
        }

        // Don't fetch if no user or no access token
        if (!user || !tokens?.accessToken) {
          set({ isLoading: false, invitations: [] });
          return;
        }

        set({ isLoading: true, error: null });

        try {
          const response =
            await WorkspaceInvitationsApi.getPendingInvitations();
          set({
            invitations: response.data || [],
            isLoading: false,
            error: null,
          });
        } catch (err) {
          console.error('Failed to fetch pending invitations:', err);
          set({
            error: err instanceof Error ? err : new Error('Unknown error'),
            invitations: [],
            isLoading: false,
          });
        }
      },
    }),
    {
      name: 'pending-invitations-store',
    },
  );

// Global refs to ensure only one fetch and polling interval
let fetchInProgress = false;
let pollingInterval: NodeJS.Timeout | null = null;
let hasInitialized = false;
let initializedComponents = 0;

export function usePendingInvitations() {
  const invitations = usePendingInvitationsStore((state) => state.invitations);
  const isLoading = usePendingInvitationsStore((state) => state.isLoading);
  const error = usePendingInvitationsStore((state) => state.error);
  const refetch = usePendingInvitationsStore((state) => state.refetch);
  const hydrated = useAuthStore((state) => state.hydrated);
  const hasInitializedRef = useRef(false);

  useEffect(() => {
    if (!hydrated) {
      return;
    }

    if (!hasInitializedRef.current) {
      hasInitializedRef.current = true;
      initializedComponents++;

      if (!hasInitialized && !fetchInProgress) {
        hasInitialized = true;
        fetchInProgress = true;
        void refetch().finally(() => {
          fetchInProgress = false;
        });
      }

      if (!pollingInterval) {
        pollingInterval = setInterval(() => {
          if (!fetchInProgress) {
            fetchInProgress = true;
            void refetch().finally(() => {
              fetchInProgress = false;
            });
          }
        }, 30000);
      }
    }

    return () => {
      if (hasInitializedRef.current) {
        initializedComponents--;
        hasInitializedRef.current = false;

        if (initializedComponents === 0 && pollingInterval) {
          clearInterval(pollingInterval);
          pollingInterval = null;
          hasInitialized = false;
        }
      }
    };
  }, [hydrated, refetch]);

  return {
    data: invitations,
    isLoading,
    error,
    refetch,
  };
}
